import { useEffect, useRef } from 'react';
import { useHandStore } from '@store/index';
import { getPerfProfile } from '@engine/hand';

/**
 * Opens the webcam and binds the stream to a <video> element.
 *
 * Accepts an `enabled` flag so the caller can defer camera acquisition
 * until after an explicit user tap — this guarantees the audio engine
 * was unlocked from the same gesture stack.
 */
export function useCamera(
  videoRef: React.RefObject<HTMLVideoElement | null>,
  enabled: boolean,
) {
  const setCameraStatus = useHandStore((s) => s.setCameraStatus);
  const streamRef = useRef<MediaStream | null>(null);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;

    const start = async () => {
      setCameraStatus('requesting');
      try {
        const profile = getPerfProfile();
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: false,
          video: {
            width: { ideal: profile.cameraWidth },
            height: { ideal: profile.cameraHeight },
            facingMode: 'user',
            frameRate: { ideal: 30 },
          },
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        const video = videoRef.current;
        if (!video) return;
        video.srcObject = stream;
        video.playsInline = true;
        video.muted = true;
        await video.play();
        setCameraStatus('streaming');
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        const denied = msg.toLowerCase().includes('denied');
        setCameraStatus(denied ? 'denied' : 'error', msg);
      }
    };

    void start();

    return () => {
      cancelled = true;
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
      setCameraStatus('idle');
    };
  }, [enabled, videoRef, setCameraStatus]);
}
